/**
 * PixiWinOverlay — Semi-transparent overlay showing win amount and multiplier.
 * Displayed during the win_presenting game phase.
 */
import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import gsap from 'gsap';

export class PixiWinOverlay {
  readonly container = new Container();

  private darkBg = new Graphics();
  private panel = new Graphics();
  private winText: Text;
  private multiplierText: Text;
  private countText: Text;

  private areaWidth: number;
  private areaHeight: number;

  constructor(width: number, height: number) {
    this.areaWidth = width;
    this.areaHeight = height;

    // Dark semi-transparent background covering the full area
    this.container.addChild(this.darkBg);
    this.drawDarkBg();

    // Rounded panel behind text
    this.container.addChild(this.panel);

    // "WIN: {amount}" text
    this.winText = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: 'Inter, Arial, sans-serif',
        fontSize: 20,
        fontWeight: 'bold',
        fill: '#fbbf24', // amber-400
        align: 'center',
      }),
    });
    this.winText.anchor.set(0.5);
    this.container.addChild(this.winText);

    // "x{multiplier}" text
    this.multiplierText = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: 'Inter, Arial, sans-serif',
        fontSize: 16,
        fontWeight: 'bold',
        fill: '#fcd34d', // amber-300
        align: 'center',
      }),
    });
    this.multiplierText.anchor.set(0.5);
    this.container.addChild(this.multiplierText);

    // Win count text
    this.countText = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: 'Inter, Arial, sans-serif',
        fontSize: 12,
        fill: '#a1a1aa', // zinc-400
        align: 'center',
      }),
    });
    this.countText.anchor.set(0.5);
    this.container.addChild(this.countText);

    this.container.visible = false;
  }

  show(totalWin: number, multiplier: number, winCount: number): void {
    this.winText.text = `WIN: ${totalWin.toLocaleString()}`;
    this.multiplierText.text = multiplier > 0 ? `x${multiplier} Multiplier` : '';
    this.multiplierText.visible = multiplier > 0;
    this.countText.text = `${winCount} winning group${winCount > 1 ? 's' : ''}`;

    this.layoutTexts();
    this.drawPanel();

    this.container.visible = true;
    this.container.alpha = 0;
    gsap.to(this.container, { alpha: 1, duration: 0.2, ease: 'power2.out' });
  }

  hide(): void {
    gsap.killTweensOf(this.container);
    this.container.visible = false;
    this.container.alpha = 0;
  }

  resize(width: number, height: number): void {
    this.areaWidth = width;
    this.areaHeight = height;
    this.drawDarkBg();
    if (this.container.visible) {
      this.layoutTexts();
      this.drawPanel();
    }
  }

  destroy(): void {
    gsap.killTweensOf(this.container);
    this.darkBg.destroy();
    this.panel.destroy();
    this.winText.destroy();
    this.multiplierText.destroy();
    this.countText.destroy();
    this.container.destroy({ children: true });
  }

  private drawDarkBg(): void {
    this.darkBg.clear();
    this.darkBg.rect(0, 0, this.areaWidth, this.areaHeight)
      .fill({ color: 0x000000, alpha: 0.6 });
  }

  private layoutTexts(): void {
    const cx = this.areaWidth / 2;
    const cy = this.areaHeight / 2;

    // Stack texts vertically centered
    let totalHeight = 24; // winText line height
    if (this.multiplierText.visible) totalHeight += 20;
    totalHeight += 16; // countText

    let y = cy - totalHeight / 2 + 12; // start from top of stack
    this.winText.position.set(cx, y);
    y += 22;

    if (this.multiplierText.visible) {
      this.multiplierText.position.set(cx, y);
      y += 20;
    }

    this.countText.position.set(cx, y + 4);
  }

  private drawPanel(): void {
    this.panel.clear();
    // Semi-transparent rounded panel behind text
    const panelWidth = 180;
    const panelHeight = this.multiplierText.visible ? 80 : 60;
    const cx = this.areaWidth / 2;
    const cy = this.areaHeight / 2;
    this.panel.roundRect(
      cx - panelWidth / 2,
      cy - panelHeight / 2,
      panelWidth,
      panelHeight,
      8
    ).fill({ color: 0x000000, alpha: 0.4 });
  }
}
